import React, { useCallback, useMemo, useState, useRef } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  ConnectionMode,
} from "reactflow";
import "reactflow/dist/style.css";
import { nodeTypes, NODE_PALETTE } from "./nodes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Trash2, Plus, Settings2, GripVertical, Zap, Clock, GitBranch as GitBranchIcon, Send, Database, Code } from "lucide-react";
import { EVENT_TYPES, formatEventType } from "@/data/mockData";
import { toast } from "sonner";
import NodeConfigDialog from "./NodeConfigDialog";
import ExpressionBuilder from "@/components/ExpressionBuilder";

export default function WorkflowEditor({ workflow, onBack, onSave }) {
  const [name, setName] = useState(workflow.name);
  const [description, setDescription] = useState(workflow.description);
  const [enabled, setEnabled] = useState(workflow.status === "active");
  const [nodes, setNodes, onNodesChange] = useNodesState(workflow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    workflow.edges.map((e) => ({
      ...e,
      type: "smoothstep",
      markerEnd: { type: MarkerType.ArrowClosed, color: "#124273" },
    }))
  );
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const nidRef = useRef(1000);
  const reactFlowWrapper = useRef(null);
  const [rfInstance, setRfInstance] = useState(null);

  const selectedNode = useMemo(() => nodes.find((n) => n.id === selectedId), [nodes, selectedId]);

  const onConnect = useCallback(
    (params) => {
      // If source is an ifCondition and sourceHandle is set, auto-label the edge
      let label;
      if (params.sourceHandle === "true") label = "TRUE";
      else if (params.sourceHandle === "false") label = "ELSE";
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            type: "smoothstep",
            label,
            labelBgPadding: [6, 3],
            labelBgBorderRadius: 4,
            markerEnd: { type: MarkerType.ArrowClosed, color: "#124273" },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const nodeDefaults = {
    eventTrigger: { label: "New Trigger", eventType: "credit_card_requested" },
    ifCondition: { label: "If Condition", expression: "{{ event.event_source == 'web' && event.payload.amount > 500 }}" },
    httpAction: { label: "HTTP Action", method: "POST", url: "https://api.example.com/notify", headers: [{ k: "Content-Type", v: "application/json" }], params: [], body: `{\n  "customer": "{{ event.customer_code }}"\n}`, timeout: 5000, retry: "linear_3", auth_type: "none", body_type: "json" },
    waitAction: { label: "Wait", value: 1, unit: "hours" },
    sqlEnrichment: { label: "Data Enrichment", dataset: "core_customer", variable: "enrich", query: "SELECT risk_score, segment\nFROM crm.customer\nWHERE code = :customer_code", timeout: 3000, cache_ttl: 60, on_empty: "continue" },
    customScript: { label: "Python Script", output_var: "result", max_ms: 500, code: "# Available: event, customer, enrich, secret\nrisk_adjusted = customer.risk_score * 1.15\nresult = { 'risk_adjusted': risk_adjusted }" },
  };

  const addNodeAt = (type, position) => {
    const id = `n${nidRef.current++}`;
    const pos = position || { x: 200, y: 200 };
    setNodes((nds) => nds.concat({ id, type, position: pos, data: nodeDefaults[type] }));
    return id;
  };

  const addNode = (type) => {
    // Add near center of viewport
    const wrapper = reactFlowWrapper.current;
    let pos = { x: 200 + nodes.length * 40, y: 180 };
    if (rfInstance && wrapper) {
      const rect = wrapper.getBoundingClientRect();
      pos = rfInstance.screenToFlowPosition({ x: rect.width / 2, y: rect.height / 2 });
    }
    const id = addNodeAt(type, pos);
    setSelectedId(id);
    toast.success(`Added ${type} node`);
  };

  const onDragStart = (evt, type) => {
    evt.dataTransfer.setData("application/reactflow", type);
    evt.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (evt) => { evt.preventDefault(); evt.dataTransfer.dropEffect = "move"; };

  const onDrop = (evt) => {
    evt.preventDefault();
    const type = evt.dataTransfer.getData("application/reactflow");
    if (!type || !rfInstance) return;
    const position = rfInstance.screenToFlowPosition({ x: evt.clientX, y: evt.clientY });
    const id = addNodeAt(type, position);
    setSelectedId(id);
  };

  const updateNode = (patch) => {
    setNodes((nds) => nds.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, ...patch } } : n)));
  };

  const applyDialogSave = (data) => {
    setNodes((nds) => nds.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, ...data } } : n)));
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
    toast.success("Node removed");
  };

  const handleSave = () => {
    onSave({
      ...workflow,
      name, description,
      status: enabled ? "active" : "disabled",
      nodes, edges,
      updated_at: new Date().toISOString(),
    });
    toast.success(`"${name}" saved`);
  };

  const hasAdvanced = selectedNode && ["httpAction", "sqlEnrichment", "customScript", "ifCondition"].includes(selectedNode.type);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-background/60 backdrop-blur-md">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="editor-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <div className="label-caps">Workflow</div>
            <input
              data-testid="editor-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-transparent font-display text-2xl font-semibold outline-none w-full truncate"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="label-caps">{enabled ? "Active" : "Disabled"}</span>
            <Switch data-testid="editor-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>
          <Button onClick={handleSave} data-testid="editor-save" className="gap-2 bg-primary hover:bg-[#0B2B4E]">
            <Save className="w-3.5 h-3.5" /> Save workflow
          </Button>
        </div>
      </div>

      {/* Editor */}
      <div className="flex flex-1 min-h-0">
        {/* Left palette */}
        <aside className="w-64 border-r border-border p-4 overflow-auto shrink-0 bg-card" data-testid="editor-palette">
          <div className="label-caps mb-3">Activities · drag to canvas</div>
          <div className="space-y-2">
            {NODE_PALETTE.map((p) => (
              <div
                key={p.type}
                draggable
                onDragStart={(e) => onDragStart(e, p.type)}
                onClick={() => addNode(p.type)}
                data-testid={`palette-${p.type}`}
                className="cursor-grab active:cursor-grabbing rounded-md border border-border hover:border-primary/50 hover:bg-secondary/50 p-2.5 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <GripVertical className="w-3 h-3 text-muted-foreground" />
                  <p.icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
                  <span className="text-sm font-medium">{p.label}</span>
                  <Plus className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
                </div>
                <div className="text-[0.7rem] text-muted-foreground leading-snug pl-5">{p.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 label-caps mb-2">Description</div>
          <Textarea
            data-testid="editor-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="text-xs"
          />

          <div className="mt-6 p-3 border border-border rounded-md bg-secondary/40">
            <div className="label-caps mb-1.5">How to connect</div>
            <p className="text-[0.7rem] text-muted-foreground leading-snug">
              Hover on a node to reveal handles on its left &amp; right edges. Drag from the right handle of one node to the left handle of another to link activities.
            </p>
          </div>
        </aside>

        {/* Canvas */}
        <div
          className="flex-1 relative"
          ref={reactFlowWrapper}
          onDrop={onDrop}
          onDragOver={onDragOver}
          data-testid="workflow-canvas"
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setRfInstance}
            onNodeClick={(_, node) => setSelectedId(node.id)}
            onNodeDoubleClick={(_, node) => {
              setSelectedId(node.id);
              if (["httpAction", "sqlEnrichment", "customScript", "ifCondition"].includes(node.type)) {
                setDialogOpen(true);
              }
            }}
            onPaneClick={() => setSelectedId(null)}
            fitView
            fitViewOptions={{ padding: 0.35, maxZoom: 0.9, minZoom: 0.5 }}
            minZoom={0.3}
            maxZoom={1.6}
            connectionMode={ConnectionMode.Loose}
            defaultEdgeOptions={{ animated: true, type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed, color: "#124273" } }}
            connectionLineStyle={{ stroke: "#124273", strokeWidth: 2 }}
          >
            <Background color="hsl(var(--grid-line))" gap={24} size={1} />
            <Controls showInteractive={false} />
            <MiniMap
              nodeColor={() => "#124273"}
              maskColor="hsl(var(--background) / 0.6)"
              pannable
              zoomable
            />
          </ReactFlow>
        </div>

        {/* Right config */}
        <aside className="w-96 border-l border-border overflow-auto shrink-0 bg-card" data-testid="editor-config-panel">
          {!selectedNode ? (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-md bg-secondary flex items-center justify-center mx-auto mb-3">
                  <Settings2 className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div className="label-caps mb-2">No selection</div>
                <div className="text-sm text-muted-foreground max-w-[16rem] mx-auto leading-relaxed">
                  Click a node on the canvas to edit its properties, or drag an activity from the palette.
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Sidebar header */}
              <div className="px-6 py-5 border-b border-border bg-secondary/40 sticky top-0 z-10 backdrop-blur-md">
                <div className="label-caps mb-1">Configure activity</div>
                <div className="font-display text-lg font-semibold leading-tight">{selectedNode.data.label}</div>
                <div className="text-xs text-muted-foreground font-mono mt-1">{typeLabel(selectedNode.type)}</div>
              </div>

              <div className="px-6 pb-6">
                {/* General section */}
                <section className="config-section">
                  <div className="config-section-title">
                    <Settings2 className="w-3 h-3" /> General
                  </div>
                  <div className="space-y-4">
                    <Field label="Node label">
                      <Input
                        value={selectedNode.data.label || ""}
                        onChange={(e) => updateNode({ label: e.target.value })}
                        data-testid="config-label"
                      />
                    </Field>
                  </div>
                </section>

                {/* Type-specific section */}
                <section className="config-section">
                  <div className="config-section-title">
                    {selectedNode.type === "eventTrigger" && <><Zap className="w-3 h-3" /> Trigger</>}
                    {selectedNode.type === "waitAction" && <><Clock className="w-3 h-3" /> Delay</>}
                    {selectedNode.type === "ifCondition" && <><GitBranchIcon className="w-3 h-3" /> Branching</>}
                    {selectedNode.type === "httpAction" && <><Send className="w-3 h-3" /> HTTP Request</>}
                    {selectedNode.type === "sqlEnrichment" && <><Database className="w-3 h-3" /> SQL Enrichment</>}
                    {selectedNode.type === "customScript" && <><Code className="w-3 h-3" /> Python Script</>}
                  </div>

                  {selectedNode.type === "eventTrigger" && (
                    <div className="space-y-4">
                      <Field label="Event type">
                        <Select value={selectedNode.data.eventType} onValueChange={(v) => updateNode({ eventType: v })}>
                          <SelectTrigger data-testid="config-eventtype"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {EVENT_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>{formatEventType(t)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <div className="text-xs text-muted-foreground bg-secondary/60 border border-border rounded-md p-3 leading-relaxed">
                        Fires when a matching <code className="font-mono text-primary">{selectedNode.data.eventType}</code> event is ingested.
                      </div>
                    </div>
                  )}

                  {selectedNode.type === "waitAction" && (
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Duration">
                        <Input type="number" value={selectedNode.data.value} onChange={(e) => updateNode({ value: Number(e.target.value) })} data-testid="config-wait-value" />
                      </Field>
                      <Field label="Unit">
                        <Select value={selectedNode.data.unit} onValueChange={(v) => updateNode({ unit: v })}>
                          <SelectTrigger data-testid="config-wait-unit"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="seconds">Seconds</SelectItem>
                            <SelectItem value="minutes">Minutes</SelectItem>
                            <SelectItem value="hours">Hours</SelectItem>
                            <SelectItem value="days">Days</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  )}

                  {selectedNode.type === "ifCondition" && (
                    <div className="space-y-4">
                      <ExpressionBuilder
                        label="Condition expression"
                        value={selectedNode.data.expression || ""}
                        onChange={(v) => updateNode({ expression: v })}
                        rows={4}
                        testId="config-expression"
                      />
                      <div className="rounded-md border border-border overflow-hidden">
                        <div className="grid grid-cols-2 divide-x divide-border">
                          <div className="p-3 bg-primary/5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-primary mb-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary" /> IF TRUE
                            </div>
                            <div className="text-[0.7rem] text-muted-foreground">Top handle → routes matching events</div>
                          </div>
                          <div className="p-3 bg-red-500/5">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-red-500 mb-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> ELSE
                            </div>
                            <div className="text-[0.7rem] text-muted-foreground">Bottom handle → alternate path</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {["httpAction", "sqlEnrichment", "customScript"].includes(selectedNode.type) && (
                    <div className="space-y-3">
                      {selectedNode.type === "httpAction" && (
                        <div className="grid grid-cols-[80px_1fr] gap-2 text-xs">
                          <span className="text-muted-foreground py-2">Method</span>
                          <span className="font-mono text-primary py-2 border border-border rounded-sm px-2 truncate">{selectedNode.data.method}</span>
                          <span className="text-muted-foreground py-2">URL</span>
                          <span className="font-mono py-2 border border-border rounded-sm px-2 truncate text-[0.7rem]">{selectedNode.data.url}</span>
                        </div>
                      )}
                      {selectedNode.type === "sqlEnrichment" && (
                        <div className="grid grid-cols-[80px_1fr] gap-2 text-xs">
                          <span className="text-muted-foreground py-2">Dataset</span>
                          <span className="font-mono text-primary py-2 border border-border rounded-sm px-2 truncate">{selectedNode.data.dataset}</span>
                          <span className="text-muted-foreground py-2">Variable</span>
                          <span className="font-mono py-2 border border-border rounded-sm px-2 truncate">{selectedNode.data.variable}</span>
                        </div>
                      )}
                      {selectedNode.type === "customScript" && (
                        <div className="text-xs text-muted-foreground bg-secondary/60 border border-border rounded-md p-3 font-mono max-h-24 overflow-auto">
                          {(selectedNode.data.code || "").split("\n").slice(0, 4).join("\n")}
                        </div>
                      )}
                      <Button
                        onClick={() => setDialogOpen(true)}
                        className="w-full gap-2 bg-primary hover:bg-[#0B2B4E]"
                        data-testid="config-open-advanced"
                      >
                        <Settings2 className="w-3.5 h-3.5" /> Open advanced editor
                      </Button>
                    </div>
                  )}
                </section>

                {/* Danger zone */}
                <section className="config-section">
                  <div className="config-section-title text-red-500">
                    <Trash2 className="w-3 h-3" /> Danger zone
                  </div>
                  <Button
                    variant="outline"
                    onClick={deleteSelected}
                    className="w-full text-destructive border-destructive/30 hover:bg-destructive/10 gap-2"
                    data-testid="config-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove this node
                  </Button>
                </section>
              </div>
            </>
          )}
        </aside>
      </div>

      {/* Advanced dialog */}
      <NodeConfigDialog
        open={dialogOpen && hasAdvanced}
        onOpenChange={setDialogOpen}
        node={selectedNode}
        onSave={applyDialogSave}
      />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="label-caps">{label}</Label>
    {children}
  </div>
);

const typeLabel = (t) => ({
  eventTrigger: "Trigger · Event",
  ifCondition: "Branch · If Condition",
  httpAction: "Action · HTTP Request",
  waitAction: "Delay · Wait",
  sqlEnrichment: "Enrichment · SQL",
  customScript: "Compute · Python",
}[t] || t);
